import { addLocale, updateLocaleOption } from "primereact/api";

/**
  The PrimeReact Calendar (Datepicker) component requires manual definition of "locales".
  Currently only supports languages the user can select in the UI (Admin -> Users -> User):
  - English (default, no setup needed)
  - French
  - German
  - Italian
  - Japanese
  - Korean
  - Spanish

  Everything derivable from CLDR (day/month names, date format, month-year order) is generated
  from the browser's Intl API so the data never goes stale and new languages only need to be
  added to `supportedLocales`. Only the UI copy below has to be translated by hand.
*/

const DAYS_IN_WEEK = 7;
const DEFAULT_LOCALE = "en";
const MONTHS_IN_YEAR = 12;
const REFERENCE_DAY = 22;
const REFERENCE_MONTH = 10;
const REFERENCE_SUNDAY = 5; // January 5, 2025 is a Sunday, matching PrimeReact's Sunday-first arrays
const REFERENCE_YEAR = 2025;
const UTC = "UTC"; // Formatting in UTC keeps the reference dates from shifting a day in negative offsets

const supportedLocales = ["de", "es", "fr", "it", "ja", "ko"] as const;

type SupportedLocale = typeof supportedLocales[number];

interface LocaleStrings {
  chooseDate: string;
  chooseMonth: string;
  chooseYear: string;
  clear: string;
  now: string;
  today: string;
  weekHeader: string;
}

const localeStrings: Record<SupportedLocale, LocaleStrings> = {
  de: {
    chooseDate: "Datum auswählen",
    chooseMonth: "Monat auswählen",
    chooseYear: "Jahr auswählen",
    clear: "Löschen",
    now: "Jetzt",
    today: "Heute",
    weekHeader: "KW",
  },
  es: {
    chooseDate: "Elegir fecha",
    chooseMonth: "Elegir mes",
    chooseYear: "Elegir año",
    clear: "Borrar",
    now: "Ahora",
    today: "Hoy",
    weekHeader: "Sem",
  },
  fr: {
    chooseDate: "Choisir une date",
    chooseMonth: "Choisir un mois",
    chooseYear: "Choisir une année",
    clear: "Effacer",
    now: "Maintenant",
    today: "Aujourd'hui",
    weekHeader: "Sem",
  },
  it: {
    chooseDate: "Scegli la data",
    chooseMonth: "Scegli il mese",
    chooseYear: "Scegli l'anno",
    clear: "Cancella",
    now: "Adesso",
    today: "Oggi",
    weekHeader: "Sett",
  },
  ja: {
    chooseDate: "日付を選択",
    chooseMonth: "月を選択",
    chooseYear: "年を選択",
    clear: "クリア",
    now: "現在",
    today: "今日",
    weekHeader: "週",
  },
  ko: {
    chooseDate: "날짜 선택",
    chooseMonth: "월 선택",
    chooseYear: "연도 선택",
    clear: "지우기",
    now: "현재",
    today: "오늘",
    weekHeader: "주",
  },
};

/**
  PrimeReact counts the first day of the week from 0 (Sunday) through 6 (Saturday). `Intl.Locale`
  exposes this through `getWeekInfo()`, but that only landed in Firefox 153, so it stays hardcoded
  until the browser floor allows deriving it.
*/
const firstDaysOfWeek: Record<SupportedLocale, number> = {
  de: 1,
  es: 1,
  fr: 1,
  it: 1,
  ja: 0,
  ko: 0,
};

/**
  PrimeReact parses dates with jQuery UI style tokens, where `M` renders an abbreviated month
  name and `yy` a four digit year. Anything that is not a token is passed through verbatim, so
  literals containing a token character have to be quoted (and a literal quote doubled) to
  survive parsing.

  The abbreviated month matches how the date range labels are formatted, and leaves English as
  "Nov 22, 2025" while still ordering every other language the way its locale writes dates.

  `M` resolves to monthNamesShort, which is CLDR's standalone set because the month picker needs
  it. CLDR's in-context set differs for German only, and differs per month ("Mär"/"März",
  "Jun"/"Juni", "Aug"/"Aug."), so no format literal can bridge the two — German therefore renders
  "7. Aug 2026" where Intl itself writes "7. Aug. 2026". Deliberate, not a rounding error.
*/
const dateFormatTokens: Record<string, string> = {
  day: "d",
  month: "M",
  year: "yy",
};

const MONTH_NUMBER_TOKEN = "m";
const numericMonthPattern = /^\d+$/;
const tokenCharacters = /['!@Ddmoy]/;

const escapeDateFormatLiteral = (literal: string) => {
  return tokenCharacters.test(literal)
    ? `'${literal.replaceAll("'", "''")}'`
    : literal;
};

const getDayNames = (
  locale: string,
  weekday: Intl.DateTimeFormatOptions["weekday"]
) => {
  const { format } = new Intl.DateTimeFormat(locale, {
    timeZone: UTC,
    weekday,
  });

  return Array.from({ length: DAYS_IN_WEEK }, (_, dayIndex) =>
    format(Date.UTC(REFERENCE_YEAR, 0, REFERENCE_SUNDAY + dayIndex))
  );
};

const getMonthNames = (
  locale: string,
  month: Intl.DateTimeFormatOptions["month"]
) => {
  const { format } = new Intl.DateTimeFormat(locale, { month, timeZone: UTC });

  return Array.from({ length: MONTHS_IN_YEAR }, (_, monthIndex) =>
    format(Date.UTC(REFERENCE_YEAR, monthIndex, 1))
  );
};

const getDateFormat = (locale: string) => {
  const parts = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    timeZone: UTC,
    year: "numeric",
  }).formatToParts(Date.UTC(REFERENCE_YEAR, REFERENCE_MONTH, REFERENCE_DAY));

  let dateFormat = "";

  for (const { type, value } of parts) {
    // Japanese writes the month as a bare number and keeps the 月 marker in the literal that
    // follows, but monthNamesShort ("8月") already carries that marker — using the month name
    // token here would render it twice ("8月月").
    const token =
      type === "month" && numericMonthPattern.test(value)
        ? MONTH_NUMBER_TOKEN
        : dateFormatTokens[type];

    dateFormat += token ?? escapeDateFormatLiteral(value);
  }

  return dateFormat;
};

/**
  Japanese and Korean write the year before the month, which PrimeReact's month picker
  and header expect to be flagged explicitly.
*/
const getShowMonthAfterYear = (locale: string) => {
  const parts = new Intl.DateTimeFormat(locale, {
    month: "long",
    timeZone: UTC,
    year: "numeric",
  }).formatToParts(Date.UTC(REFERENCE_YEAR, REFERENCE_MONTH, REFERENCE_DAY));

  return (
    parts.findIndex(({ type }) => type === "year") <
    parts.findIndex(({ type }) => type === "month")
  );
};

for (const locale of supportedLocales) {
  addLocale(locale, {
    ...localeStrings[locale],
    dateFormat: getDateFormat(locale),
    dayNames: getDayNames(locale, "long"),
    dayNamesMin: getDayNames(locale, "narrow"),
    dayNamesShort: getDayNames(locale, "short"),
    firstDayOfWeek: firstDaysOfWeek[locale],
    monthNames: getMonthNames(locale, "long"),
    monthNamesShort: getMonthNames(locale, "short"),
    showMonthAfterYear: getShowMonthAfterYear(locale),
  });
}

/**
  English ships with PrimeReact, so only its date format is overridden — the built-in
  "mm/dd/yy" would otherwise be the one language not using the abbreviated month.
*/
updateLocaleOption("dateFormat", getDateFormat(DEFAULT_LOCALE), DEFAULT_LOCALE);

const registeredLocales = new Set<string>(supportedLocales);

const isRegistered = (language: string): language is SupportedLocale =>
  registeredLocales.has(language);

export type RegisteredLocale = SupportedLocale | typeof DEFAULT_LOCALE;

/**
  PrimeReact throws when a component is given a locale that was never registered, so any
  language outside the supported set — including none at all — has to resolve to its
  built-in English locale.

  Expects a primary language subtag ("de"), not a regional tag ("de-DE"), which is what every
  producer of a language in the app stores.
*/
export const getRegisteredLocale = (language?: string): RegisteredLocale => {
  return language && isRegistered(language) ? language : DEFAULT_LOCALE;
};
